import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, ArrowUpRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import Logo from "./Logo";
import { track } from "../lib/analytics";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Programs", to: "/programs" },
  { label: "Classes", to: "/classes" },
  { label: "Camps", to: "/camps" },
  { label: "Play-Based Support", to: "/play-based-support" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-2xl bg-white/85 border-b border-slate-200/60"
          : "bg-white/60 backdrop-blur-xl"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <Logo to="/" />

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-3.5 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? "text-slate-900"
                      : "text-slate-600 hover:text-slate-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {n.label}
                    {isActive && (
                      <span className="absolute -bottom-1 left-0 right-0 h-[2px] rounded-full bg-[#FF8C42]" />
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Link
              to="/trial-class"
              data-testid="header-trial-cta"
              className="btn-primary !py-2.5 !px-5 text-xs"
            >
              Book a trial
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              asChild
              className="lg:hidden"
              data-testid="mobile-nav-trigger"
            >
              <button
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-900"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="mobile-nav-sheet"
              className="w-[85vw] max-w-sm bg-white p-0 border-l border-slate-200"
            >
              <SheetHeader className="px-6 py-5 border-b border-slate-100">
                <SheetTitle className="font-display text-left text-xl font-extrabold">
                  Nexora
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-1 p-4 overflow-y-auto">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center justify-between rounded-2xl px-4 py-3.5 text-base font-semibold transition-colors ${
                        isActive
                          ? "bg-slate-900 text-white"
                          : "text-slate-800 hover:bg-slate-100"
                      }`
                    }
                  >
                    {n.label}
                    <ArrowUpRight className="h-4 w-4 opacity-60" />
                  </NavLink>
                ))}
              </div>
              <div className="p-4 mt-2 border-t border-slate-100">
                <Link
                  to="/trial-class"
                  onClick={() => track("book_trial_clicked", { cta_target: "/trial-class" })}
                  data-testid="mobile-header-trial-cta"
                  className="btn-primary w-full"
                >
                  Book a trial
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
